/**
 * RoleSummary — THE shared role-reference render (DEV-1424 / DEV-1425,
 * SPEC_ROLE_INVITATION_PREVIEW.md §2, SCOPING_ROLE_DESCRIPTION_SURFACES.md v2).
 *
 * One canonical description record, rendered everywhere via this single
 * component — two views, never two copies. Consumers:
 *
 * 1. **Invitation preview** (ActionItemTray, variant "preview") — "Review this
 *    role before you accept." The invitee does NOT yet hold the role, so the
 *    description + responsibilities resolve from the role DEFINITION via
 *    useInvitationRoleSummary below (slot → definition → rhythm details).
 * 2. **About this Role** (variant "full") — the held-role reference view:
 *    description → duties/rhythm (live RhythmDisplayPanel) → team roster.
 *    Rendered today in the Action Center role detail; relocates to the
 *    Memberships Tray in the §7 restructuring (Track 4 Slices C/D) riding
 *    this same component.
 *
 * Fences: read-only reference — NO grant/assign/edit controls, ever (those
 * stay on the Team surface); Resign is a belonging action and lives OUTSIDE
 * this component (§7.1). Content must read as "what the role is," never a
 * task list (§7.5c).
 */

import React, { useEffect, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { fetchRoleSlots } from "../../api/communityRoleSlots";
import { fetchRhythmDetailsForDefinition } from "../../api/roleRhythms";
import RhythmDisplayPanel from "../../Pages/Engagements/RhythmDisplayPanel";
import FlipTeamMemberCard from "../TeamMemberCard/FlipTeamMemberCard";
import styles from "./RoleSummary.module.css";

export interface RoleSummaryResponsibility {
  name: string;
  /** true = cadenced recurring task; false = standing duty. */
  recurring: boolean;
}

export interface RoleSummaryTeamMember {
  key: string;
  userGuid: string | null;
  name: string;
  avatar: string | null;
  roleTitle: string;
  isCurrentUser: boolean;
  email?: string | null;
  profileUrl?: string | null;
}

export interface RoleSummaryProps {
  /**
   * "preview" — invitation tray: compact, heading + description +
   * responsibilities list (role not yet held; team omitted by design —
   * SPEC §3 "optional/condensed").
   * "full" — held-role reference: description → live rhythm panel → team.
   */
  variant: "preview" | "full";
  /** Optional heading above the summary (e.g. "Review this role before you accept"). */
  heading?: string;
  /** Role display label (full variant renders it as its own section). */
  roleLabel?: string;
  /** Plain-language role description. */
  description?: string;
  /** Pre-resolved responsibilities (preview variant — from the definition's
   *  rhythm details; standing duties + recurring tasks, each tagged). */
  responsibilities?: RoleSummaryResponsibility[];
  /** Live rhythm panel inputs (full variant — role held). */
  sourceDefinitionGuid?: string | null;
  slotGuid?: string | null;
  entityIsPerpetual?: boolean;
  /** Team roster (full variant). Condensed empty state when it's just you. */
  teamMembers?: RoleSummaryTeamMember[];
}

/**
 * Invitation-side data resolution (role not yet held): slot → definition →
 * rhythm details. Extracted verbatim from ActionItemTray's inline effect
 * (DEV-1424) so the preview and any future pre-accept surface resolve
 * identically. Best-effort — returns empty on any failure.
 */
export function useInvitationRoleSummary(
  active: boolean,
  communityGuid: string | null | undefined,
  roleSlotGuid: string | null | undefined,
  /** Reset key — pass the item id so a different invitation re-resolves. */
  resetKey?: string,
): { description: string; responsibilities: RoleSummaryResponsibility[] } {
  const [description, setDescription] = useState<string>("");
  const [responsibilities, setResponsibilities] = useState<RoleSummaryResponsibility[]>([]);

  useEffect(() => {
    let cancelled = false;
    setDescription("");
    setResponsibilities([]);
    if (!active || !communityGuid || !roleSlotGuid) return;
    (async () => {
      try {
        const slots = await fetchRoleSlots(String(communityGuid));
        const slot = slots.find(
          (s) => String(s.roleSlotGuid).toUpperCase() === String(roleSlotGuid).toUpperCase(),
        );
        const sdg = slot?.sourceDefinitionGuid;
        if (!sdg) return;
        const details = await fetchRhythmDetailsForDefinition(String(sdg));
        if (cancelled || !details) return;
        // DEV-1424 — the preview shows the role's own description plus a fuller
        // "Responsibilities" list: standing duties AND recurring tasks, each
        // tagged, so an invitee sees the full commitment before accepting
        // (informed consent).
        const resolved = (details.tasks || [])
          .filter((tk) => (tk.name || "").trim())
          .map((tk) => ({ name: tk.name, recurring: tk.taskKind !== "standing_duty" }));
        if (!cancelled) {
          setDescription((details.description || "").trim());
          setResponsibilities(resolved);
        }
      } catch {
        /* best-effort — duties just don't show if anything fails */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, communityGuid, roleSlotGuid, resetKey]);

  return { description, responsibilities };
}

const RoleSummary: React.FC<RoleSummaryProps> = ({
  variant,
  heading,
  roleLabel,
  description,
  responsibilities,
  sourceDefinitionGuid,
  slotGuid,
  entityIsPerpetual,
  teamMembers,
}) => {
  const { t } = useAppTranslation("pages");

  if (variant === "preview") {
    const hasContent = !!description || (responsibilities?.length ?? 0) > 0;
    if (!hasContent) return null;
    return (
      <div className={styles.preview}>
        {heading ? <div className={styles.previewHeading}>{heading}</div> : null}
        {description ? (
          <div className={styles.duties}>
            <div className={styles.dutiesLabel}>
              {t("actionCenter.inbox.tray.aboutRoleLabel")}
            </div>
            <p className={styles.previewText}>{description}</p>
          </div>
        ) : null}
        {(responsibilities?.length ?? 0) > 0 && (
          <div className={styles.duties}>
            <div className={styles.dutiesLabel}>
              {t("actionCenter.inbox.tray.responsibilities")}
            </div>
            <ul className={styles.dutiesList}>
              {responsibilities!.map((r, i) => (
                <li key={i}>
                  {r.name}
                  {r.recurring ? (
                    <span className={styles.recurringTag}>
                      {t("actionCenter.inbox.tray.recurringTag")}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  // variant === "full" — the held-role reference view.
  return (
    <>
      {heading ? <div className={styles.previewHeading}>{heading}</div> : null}
      {description ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t("engagements.roleDescription")}</div>
          <div className={styles.sectionText}>{description}</div>
        </div>
      ) : null}
      {roleLabel ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t("actionCenter.inbox.col.role")}</div>
          <div className={styles.sectionText}>{roleLabel}</div>
        </div>
      ) : null}
      {sourceDefinitionGuid ? (
        <RhythmDisplayPanel
          sourceDefinitionGuid={sourceDefinitionGuid}
          slotGuid={slotGuid ?? null}
          entityIsPerpetual={!!entityIsPerpetual}
        />
      ) : (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t("engagements.tabDuties")}</div>
          <div className={styles.sectionText}>
            {t("actionCenter.inbox.roleTab.descEmpty")}
          </div>
        </div>
      )}
      {teamMembers ? (
        <div className={styles.section}>
          <div className={styles.sectionLabel}>{t("actionCenter.inbox.roleTab.team")}</div>
          {teamMembers.length === 0 ||
          (teamMembers.length === 1 && teamMembers[0].isCurrentUser) ? (
            <div className={styles.sectionText}>{t("engagements.teamEmpty")}</div>
          ) : (
            <div className={styles.teamGrid}>
              {teamMembers.map((m) => (
                <FlipTeamMemberCard
                  key={m.key}
                  displayName={m.name}
                  photoUrl={m.avatar}
                  roleTitle={m.roleTitle}
                  userGuid={m.userGuid}
                  linkedUserGuid={m.userGuid}
                  contactEmail={m.email ?? undefined}
                  contactProfileUrl={m.profileUrl ?? undefined}
                  splitName
                  size="md"
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </>
  );
};

export default RoleSummary;
